/*
 * Authors: mariushirjanu
 * Created: 29/11/2022
 */

import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { UIRouterModule } from '@uirouter/angular';
import {
  STATE_ORDERENTRY_CATALOG,
  STATE_ORDERENTRY_CATALOG_DETAIL,
  STATE_ORDERENTRY_CATALOG_OVERVIEW,
  STATE_ORDERENTRY_DOCUMENTS,
  STATE_ORDERENTRY_CHARGES,
  STATE_ORDERENTRY_INVOICE,
  STATE_ORDERENTRY_ORDER_INFO,
  STATE_ORDERENTRY_PAYMENT,
  STATE_ORDERENTRY_SHIPPING,
  STATE_ORDERENTRY_WEBSHOP,
  STATE_ORDERENTRY_CONFIRM,
  STATE_ORDERENTRY_OVERVIEW, STATE_ORDERENTRY, STATE_ORDERENTRY_CATALOG_GUIDE, STATE_ORDERENTRY_CART
} from '@app/shared/app-states';
import { OrderEntryWebshopComponent } from './webshop/order-entry-webshop.component';
import { ACTION_CODE_GOTOSUBMIT, ACTION_CODE_SAVEFORLATER, ACTION_CODE_SUBMIT } from './order-entry.constants';
import { Transition } from '@uirouter/angular';
import { OrderEntryRequesterDataService } from './requester/order-entry-requester-data.service';
import { PriceCartService } from '../cart/price-cart.service';
import { OrderEntryRequesterService } from './requester/order-entry-requester.service';
import { SharedModule } from '@app/shared/shared.module';
import { ShippingInfoComponent } from '../shipping/shipping-info.component';
import { ShippingDetailsComponent } from '../shipping/shipping-details.component';
import { LocationInfoComponent } from './location-info/location-info.component';
import { OrderCatalogOverviewComponent } from '../catalog/overview/order-catalog-overview.component';
import { OrderEntryCatalogComponent } from './catalog/order-entry-catalog.component';
import { OrderCatalogDetailComponent } from '../catalog/detail/order-catalog-detail.component';
import { OrderSharedModule } from '../order-shared.module';
import { OrderEntryInfoComponent } from './order-info/order-entry-info.component';
import { OrderEntryPaymentComponent } from './payment/order-entry-payment.component';
import { OrderEntryShippingComponent } from './location/shipping/order-entry-shipping.component';
import { OrderLocationBillingTypeComponent } from './location/invoice/order-location-billing-type.component';
import { OrderLocationAddressComponent } from './location/address/order-location-address.component';
import { OrderLocationEinvoiceComponent } from './location/invoice/order-location-einvoice.component';
import { OrderLocationContactComponent } from './location/order-location-contact.component';
import { OrderLocationCommentsComponent } from './location/order-location-comments.component';
import { OrderEntryInvoiceComponent } from './location/invoice/order-entry-invoice.component';
import { OrderLocationOvernightComponent } from './location/shipping/order-location-overnight.component';
import { OrderEntryInvoiceService } from './location/invoice/order-entry-invoice.service';
import { OrderEntryShippingService } from './location/shipping/order-entry-shipping.service';
import { OrderEntryLocationService } from './location/order-entry-location.service';
import { OrderEntryLocationDataService } from './location/order-entry-location-data.service';
import { OrderEntryLocationFormService } from './location/order-entry-location-form.service';
import { OrderLocationAddressCurrentComponent } from './location/address/order-location-address-current.component';
import { OrderLocationAddressExistingComponent } from './location/address/order-location-address-existing.component';
import { BaseLocationAddressComponent } from './location/address/base-location-address.component';
import { OrderLocationAddressRecentComponent } from './location/address/order-location-address-recent.component';
import { OrderEntryDocumentsComponent } from './documents/order-entry-documents.component';
import { OrderEntryDocumentsDataService } from './documents/order-entry-documents-data.service';
import { OrderEntryAddDocumentDialogComponent } from './documents/order-entry-add-document-dialog.component';
import { OrderEntryDocumentsService } from './documents/order-entry-documents.service';
import { AgDocumentDetailRendererComponent } from './documents/ag-document-detail-renderer.component';
import { OrderEntryChargesComponent } from './charges/order-entry-charges.component';
import { OrderEntryChargesDataService } from './charges/order-entry-charges-data.service';
import { OrderEntryChargesService } from './charges/order-entry-charges.service';
import { OrderEntryConfirmComponent } from './confirm/order-entry-confirm.component';
import { ConfirmTcLabelComponent } from './confirm/confirm-tc-label.component';
import { OrderEntryOverviewItemsComponent } from './order-entry-overview-items.component';
import { OrderEntryConfirmService } from './confirm/order-entry-confirm.service';
import { OrderEntryOverviewComponent } from './overview/order-entry-overview.component';
import { OrderBPOverviewComponent } from './overview/order-bp-overview.component';
import { OrderEntryService } from './order-entry.service';
import { OrderEntryComponent } from './order-entry.component';
import { CatalogGuideComponent } from '../catalog/guide/catalog-guide.component';
import { CartComponent } from '../cart/cart.component';
import { OrderEntryCancelDialogComponent } from './order-entry-cancel-dialog.component';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { TranslationCategory } from '@app/shared/services/ga-state.service';
import { GaStateDeclaration } from '@type_definitions/datatypes';
import { TRANSLATION_READY_RESOLVER } from '@app/app-routes.config';

export const catalogSearchParams:string = 'layout&pmin&pmax&sort&num&page&terms&cat&guide&filters&instock&fav&type';

const modules:TranslationCategory[] = [ TranslationCategory.ORDERENTRY, TranslationCategory.CATALOG ]; // load orderentry translation keys
const validateOnAllActions:string[] = [
  ACTION_CODE_SAVEFORLATER,
  ACTION_CODE_SUBMIT,
  ACTION_CODE_GOTOSUBMIT
];

const validateOnSubmit: string[] = [
  ACTION_CODE_SUBMIT,
  ACTION_CODE_GOTOSUBMIT
];

const resolveOrderIdParam = {
  token: 'orderId',
  deps: [Transition], resolveFn: (transition:Transition) => {
    return Number(transition.params().order) || undefined;
  }
};

const states:GaStateDeclaration[] = [
  {
    name: STATE_ORDERENTRY,
    url: '/orderentry?order&stockOrderType&new&referrer&debug',
    params: {
      order: {
        dynamic: true
      }
    },
    abstract: true,
    views: {
      content: {
        component: OrderEntryComponent
      }
    },
    data: {
      translate: true,
      modules
    },
    resolve: [
      resolveOrderIdParam,
      {
        token: 'new', deps: [Transition], resolveFn: (transition:Transition) => transition.params().new
      }, {
        token: 'referrer', deps: [Transition], resolveFn: (transition:Transition) => transition.params().referrer
      }, {
        token: 'debug', deps: [Transition], resolveFn: (transition:Transition) => !!transition.params().debug
      }, {
        token: 'stockOrderType', deps: [ Transition ], resolveFn: (trans:Transition) => trans.params().stockOrderType
      },
      TRANSLATION_READY_RESOLVER
    ]
  },
  {
    name: STATE_ORDERENTRY_WEBSHOP,
    url: '',
    component: OrderEntryWebshopComponent,
    data: {
      viewTitle: 'Order - Select Webshop',
      validate: validateOnAllActions,
      modules
    },
    resolve: [
      resolveOrderIdParam
    ]
  },

  {
    name: STATE_ORDERENTRY_CATALOG,
    url: '/catalog',
    abstract: true,
    component: OrderEntryCatalogComponent,
    resolve: [ {
      token: 'name', deps: [ Transition ], resolveFn: (trans:Transition) => trans.to().name
    }, {
      token: 'stockOrderType', deps: [ Transition ], resolveFn: (trans:Transition) => trans.params().stockOrderType
    }, {
      token: 'punchoutMessageId', deps: [ Transition ], resolveFn: (trans:Transition) => trans.params().punchoutMessageId
    } ]
  },
  {
    name: STATE_ORDERENTRY_CATALOG_OVERVIEW,
    url: `?${catalogSearchParams}&compat`,
    data: {
      viewTitle: 'Order - Catalog'
    },
    component: OrderCatalogOverviewComponent
  },
  {
    name: STATE_ORDERENTRY_CATALOG_DETAIL,
    url: '/:product?bundle&compat&guide',
    data: {
      viewTitle: 'Order - Product Details'
    },
    component: OrderCatalogDetailComponent
  },
  {
    name: STATE_ORDERENTRY_CATALOG_GUIDE,
    url: '/guide',
    component: CatalogGuideComponent,
    data: {
      viewTitle: 'Order - Buying Guide'
    }
  },
  {
    name: STATE_ORDERENTRY_CART,
    url: '/cart',
    data: {
      viewTitle: 'Order - Cart',
      validate: [
        ACTION_CODE_SUBMIT,
        ACTION_CODE_GOTOSUBMIT
      ]
    },
    component: CartComponent
  },
  {
    name: STATE_ORDERENTRY_ORDER_INFO,
    url: '/info',
    data: {
      viewTitle: 'Order - Order Info',
      validate: validateOnSubmit
    },
    resolve: [
      {
        token: 'stockOrderType',
        deps: [ Transition ],
        resolveFn: (trans:Transition) => {
          return trans.params().stockOrderType;
        }
      }
    ],
    component: OrderEntryInfoComponent
  },
  {
    name: STATE_ORDERENTRY_PAYMENT,
    url: '/payment?errorMessage',
    data: {
      viewTitle: 'Order - Payment',
      validate: validateOnSubmit
    },
    resolve: [
      {
        token: 'errorMessage',
        deps: [ Transition ],
        resolveFn: (trans:Transition) => {
          return trans.params().errorMessage;
        }
      }
    ],
    component: OrderEntryPaymentComponent
  },
  {
    name: STATE_ORDERENTRY_SHIPPING,
    url: '/shipto',
    data: {
      viewTitle: 'Order - Shipping',
      validate: validateOnAllActions // SAVEFORLATER we only need the beforeValidate for this
    },
    component: OrderEntryShippingComponent
  },
  {
    name: STATE_ORDERENTRY_INVOICE,
    url: '/billto',
    data: {
      viewTitle: 'Order - Invoice',
      validate: validateOnAllActions // SAVEFORLATER we only need the beforeValidate for this
    },
    component: OrderEntryInvoiceComponent
  },
  {
    name: STATE_ORDERENTRY_CHARGES,
    url: '/charges',
    data: {
      validate: validateOnAllActions,
      viewTitle: 'Order - Charges'
    },
    component: OrderEntryChargesComponent
  },
  {
    name: STATE_ORDERENTRY_DOCUMENTS,
    url: '/documents',
    data: {
      viewTitle: 'Order - Documents'
    },
    component: OrderEntryDocumentsComponent,
    resolve: [
      { token: 'editable', resolveFn: () => true },
      { token: 'showUpload', resolveFn: () => true },
      { token: 'showTitle', resolveFn: () => true }
    ]
  },
  {
    name: STATE_ORDERENTRY_CONFIRM,
    url: '/confirm',
    data: {
      viewTitle: 'Order - Confirmation',
      validate: validateOnSubmit
    },
    component: OrderEntryConfirmComponent
  },
  {
    name: STATE_ORDERENTRY_OVERVIEW,
    url: '/overview?paid',
    data: {
      viewTitle: 'Order - Overview'
    },
    component: OrderEntryOverviewComponent,
    resolve: [
      resolveOrderIdParam
    ]
  }
];

@NgModule({
  declarations: [
    OrderEntryComponent,
    OrderEntryWebshopComponent,
    OrderEntryCatalogComponent,
    OrderEntryInfoComponent,
    OrderEntryPaymentComponent,
    OrderEntryShippingComponent,
    OrderEntryInvoiceComponent,
    BaseLocationAddressComponent,
    OrderLocationBillingTypeComponent,
    OrderLocationAddressComponent,
    OrderLocationAddressCurrentComponent,
    OrderLocationAddressExistingComponent,
    OrderLocationAddressRecentComponent,
    OrderLocationEinvoiceComponent,
    OrderLocationContactComponent,
    OrderLocationOvernightComponent,
    OrderLocationCommentsComponent,
    ShippingInfoComponent,
    ShippingDetailsComponent,
    LocationInfoComponent,
    OrderEntryDocumentsComponent,
    OrderEntryAddDocumentDialogComponent,
    OrderEntryChargesComponent,
    AgDocumentDetailRendererComponent,
    OrderEntryConfirmComponent,
    ConfirmTcLabelComponent,
    OrderEntryOverviewItemsComponent,
    OrderEntryOverviewComponent,
    OrderBPOverviewComponent,
    OrderEntryCancelDialogComponent
  ],
  imports: [
    UIRouterModule.forChild({ states }),
    CommonModule,
    SharedModule,
    OrderSharedModule,
    BsDropdownModule
  ],
  providers: [
    PriceCartService,
    OrderEntryRequesterDataService,
    OrderEntryRequesterService,
    OrderEntryInvoiceService,
    OrderEntryShippingService,
    OrderEntryLocationService,
    OrderEntryLocationDataService,
    OrderEntryLocationFormService,
    OrderEntryDocumentsService,
    OrderEntryDocumentsDataService,
    OrderEntryChargesService,
    OrderEntryChargesDataService,
    OrderEntryConfirmService,
    OrderEntryService
  ],
  exports: [
    ShippingInfoComponent,
    ShippingDetailsComponent,
    LocationInfoComponent
  ]
})
export class OrderEntryModule { }